'use strict';

const libPictView = require('pict-view');
const libTabBar = require('./TabBar.js');

/**
 * Entitlements — the #/Entitlements screen. A left rail lists subjects (Roles / Permission Groups / Users); the
 * main pane shows the task catalog grouped by area, each task a switch you flip on/off for the selected subject.
 * All logic + data live in the EntitlementsProvider, which assigns hand-built HTML into #Ent-Rail / #Ent-Detail
 * (SamplePlan pattern) and writes grants through the RBAC grant path.
 */
class Entitlements extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider() { return this.pict.providers.Entitlements; }

	onAfterRender(pRenderable)
	{
		this.pict.CSSMap.injectCSS();
		const tmpProvider = this.provider;
		if (tmpProvider)
		{
			tmpProvider.render();
			if (!tmpProvider.state.Loaded && !tmpProvider.state.Loading) { tmpProvider.loadAsync(); }
		}
		return super.onAfterRender(pRenderable);
	}
}

module.exports = Entitlements;
module.exports.default_configuration =
{
	ViewIdentifier: 'Entitlements',
	DefaultRenderable: 'EntitlementsWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		${ libTabBar.TABBAR_CSS }
		.ent-wrap { padding: 1.25rem 1.5rem 2rem; }
		.ent-head { margin: 0 0 1rem; }
		.ent-title { font-size:1.5rem; font-weight:700; line-height:1.2; margin:0; color:var(--theme-color-text-primary,#1f2733); }
		.ent-subtitle { font-size:0.9rem; color:var(--theme-color-text-muted,#6b7686); margin:0.2rem 0 0; }
		.ent-cols { display:grid; grid-template-columns: 17rem 1fr; gap:1.1rem; align-items:start; }
		@media (max-width: 820px) { .ent-cols { grid-template-columns: 1fr; } }
		.ent-rail { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:0.6rem; max-height:42rem; display:flex; flex-direction:column; }
		.ent-rail-search { flex:0 0 auto; width:100%; box-sizing:border-box; font-size:0.86rem; padding:0.45rem 0.6rem; margin:0 0 0.55rem; border:1px solid var(--theme-color-border-medium,#d5dbe3); border-radius:7px; color:var(--theme-color-text-primary,#1f2733); background:var(--theme-color-background-input,#fff); }
		.ent-rail-list { flex:1 1 auto; min-height:0; overflow:auto; }
		.ent-rail-group { margin:0 0 1rem; }
		.ent-rail-group:last-child { margin-bottom:0; }
		.ent-rail-head { display:flex; align-items:center; gap:0.45rem; font-size:0.72rem; font-weight:800; text-transform:uppercase; letter-spacing:0.06em; color:var(--theme-color-text-secondary,#45505f); padding:0.4rem 0.55rem; margin:0 0 0.4rem; background:var(--theme-color-background-secondary,#eef1f6); border-radius:6px; position:sticky; top:0; z-index:1; }
		.ent-rail-head-icon { flex:0 0 auto; color:var(--theme-color-brand-primary,#2e7d74); font-size:0.82rem; }
		.ent-rail-head-text { flex:1 1 auto; }
		.ent-rail-count { flex:0 0 auto; min-width:1.1rem; text-align:center; font-size:0.66rem; font-weight:700; color:var(--theme-color-text-muted,#6b7686); background:var(--theme-color-background-panel,#fff); border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:9px; padding:0.02rem 0.35rem; }
		.ent-rail-item { padding:0.38rem 0.55rem; border-radius:6px; font-size:0.88rem; color:var(--theme-color-text-secondary,#45505f); cursor:pointer; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
		.ent-rail-item:hover { background:var(--theme-color-background-secondary,#eef1f6); color:var(--theme-color-text-primary,#1f2733); }
		.ent-rail-item.is-active { background:var(--theme-color-brand-primary,#2e7d74); color:#fff; font-weight:600; }
		.ent-rail-empty { font-size:0.82rem; color:var(--theme-color-text-muted,#8894a4); padding:0.3rem 0.55rem; }
		.ent-main, .ent-empty { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:1.1rem 1.25rem; min-height:22rem; }
		.ent-empty { display:flex; align-items:center; justify-content:center; text-align:center; color:var(--theme-color-text-muted,#6b7686); }
		.ent-tabs { display:flex; gap:0.25rem; border-bottom:1px solid var(--theme-color-border-light,#eef1f6); margin:0 0 0.9rem; }
		.ent-tab { font-size:0.86rem; font-weight:600; padding:0.45rem 0.9rem; border:none; background:transparent; color:var(--theme-color-text-muted,#6b7686); cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-1px; }
		.ent-tab:hover { color:var(--theme-color-text-primary,#1f2733); }
		.ent-tab.is-active { color:var(--theme-color-primary,#2f6fed); border-bottom-color:var(--theme-color-primary,#2f6fed); }
		.ent-forms-search { width:100%; max-width:26rem; font-size:0.9rem; padding:0.45rem 0.65rem; border:1px solid var(--theme-color-border-medium,#d5dbe3); border-radius:7px; box-sizing:border-box; margin:0 0 0.7rem; color:var(--theme-color-text-primary,#1f2733); background:var(--theme-color-background-input,#fff); }
		.ent-detail-head { margin:0 0 0.9rem; }
		.ent-detail-title { font-size:1.2rem; font-weight:700; color:var(--theme-color-text-primary,#1f2733); }
		.ent-detail-sub { font-size:0.8rem; color:var(--theme-color-text-muted,#6b7686); margin-top:0.15rem; }
		.ent-busy { color:var(--theme-color-primary,#2f6fed); font-weight:600; }
		.ent-note { font-size:0.84rem; color:var(--theme-color-text-muted,#6b7686); margin:0 0 0.7rem; }
		.ent-error { font-size:0.85rem; color:var(--theme-color-status-danger,#c0392b); margin:0 0 0.7rem; }
		.ent-area { margin:0 0 1.1rem; }
		.ent-area-head { font-size:0.66rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-text-muted,#6b7686); padding:0 0 0.3rem; border-bottom:1px solid var(--theme-color-border-light,#eef1f6); margin:0 0 0.4rem; }
		.ent-task { display:flex; align-items:center; gap:1rem; padding:0.45rem 0.15rem; }
		.ent-task-main { flex:1 1 auto; min-width:0; }
		.ent-task-label { font-size:0.92rem; font-weight:600; color:var(--theme-color-text-primary,#1f2733); }
		.ent-task-desc { font-size:0.8rem; color:var(--theme-color-text-muted,#6b7686); }
		.ent-flag { font-size:0.58rem; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; color:#fff; background:var(--theme-color-status-warning,#b8860b); padding:0.05rem 0.35rem; border-radius:9px; vertical-align:middle; }
		.ent-hint { font-size:0.7rem; color:var(--theme-color-text-muted,#8894a4); font-style:italic; margin-left:0.3rem; }
		.ent-switch { flex:0 0 auto; width:2.5rem; height:1.4rem; border-radius:999px; border:1px solid var(--theme-color-border-medium,#d5dbe3); background:var(--theme-color-background-tertiary,#eef1f6); cursor:pointer; position:relative; transition:background 0.12s; }
		.ent-switch-dot { position:absolute; top:0.12rem; left:0.14rem; width:1rem; height:1rem; border-radius:50%; background:#fff; box-shadow:0 1px 2px rgba(0,0,0,0.25); transition:left 0.12s; }
		.ent-switch.is-on { background:var(--theme-color-primary,#2f6fed); border-color:var(--theme-color-primary,#2f6fed); }
		.ent-switch.is-on .ent-switch-dot { left:1.22rem; }
		.ent-switch[disabled] { opacity:0.5; cursor:default; }
			.ent-mlist-row { align-items:flex-start; }
			.ent-mlist-icon { color:var(--theme-color-brand-primary,#2e7d74); font-size:0.82rem; margin-right:0.45rem; }
			.ent-mlist-read { flex:0 0 auto; display:flex; align-items:center; gap:0.5rem; padding-top:0.1rem; }
			.ent-read-lbl { font-size:0.62rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-text-muted,#6b7686); }
			.ent-mlist-adv-btn { display:inline-flex; align-items:center; gap:0.3rem; font-size:0.74rem; font-weight:600; color:var(--theme-color-primary,#2f6fed); background:none; border:none; cursor:pointer; padding:0.15rem 0 0; }
			.ent-mlist-adv-count { font-size:0.6rem; font-weight:700; color:#fff; background:var(--theme-color-primary,#2f6fed); border-radius:9px; padding:0.02rem 0.32rem; }
			.ent-mlist-adv { display:flex; flex-wrap:wrap; gap:0.55rem 1.6rem; padding:0.15rem 0 0.7rem 0.15rem; margin:0 0 0.35rem; border-bottom:1px solid var(--theme-color-border-light,#f0f2f6); }
			.ent-mlist-action { display:flex; align-items:center; gap:0.5rem; }
			.ent-mlist-action-lbl { font-size:0.8rem; color:var(--theme-color-text-secondary,#45505f); min-width:3.4rem; }
		.ent-confirm { font-size:0.82rem; color:var(--theme-color-text-secondary,#45505f); display:inline-flex; align-items:center; gap:0.4rem; }
		.ent-btn { font-size:0.78rem; font-weight:600; padding:0.28rem 0.7rem; border-radius:6px; cursor:pointer; border:1px solid transparent; }
		.ent-btn-danger { color:#fff; background:var(--theme-color-status-danger,#c0392b); }
		.ent-btn-ghost { color:var(--theme-color-text,#22303f); background:transparent; border-color:var(--theme-color-border-medium,#d5dbe3); }
	`,
	Templates:
	[
		{
			Hash: 'Entitlements-Wrap',
			Template: /*html*/`
				<div class="ent-wrap">
					<div class="ent-head">
						<h1 class="ent-title">Entitlements</h1>
						<p class="ent-subtitle">Set up what a role, permission group, or user can do — in plain tasks. Flip a task on and this grants the right permissions behind the scenes; flip it off to take them away. Broad tasks are flagged; grant those sparingly.</p>
					</div>
					<div class="ent-cols">
						<div id="Ent-Rail" class="ent-rail"></div>
						<div id="Ent-Detail" class="ent-main"></div>
					</div>
				</div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'EntitlementsWrap',
			TemplateHash: 'Entitlements-Wrap',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
			RenderMethod: 'replace',
		},
	],
};
